import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import MUIIconButton from "../UI/MUIIconButton/MUIIconButton";
import { RING_ALIGN } from "../../utils/indexScoreRingAlign";
import { INDEX_ACCENT, INDEX_TRACK, INDEX_TEXT, INDEX_TEXT_MUTED } from "../../utils/indexScoreTheme";

const SIZES = {
  large: {
    ring: 140, ringStroke: 12, totalFont: 34, outOfFont: 13,
    gap: 28, barRowGap: 16, barLabelFont: 13, barLabelWidth: 150,
    barHeight: 14, valueFont: 13, axisFont: 11, captionFont: 14,
  },
  small: {
    ring: 92, ringStroke: 9, totalFont: 23, outOfFont: 9.5,
    gap: 14, barRowGap: 13, barLabelFont: 11.5, barLabelWidth: 92,
    barHeight: 10, valueFont: 11, axisFont: 9.5, captionFont: 10,
  },
};

export default function CompositeIndexScoreChart({ multiFactor, size = "large", onInfoClick, iconId, sortOrder }) {
  if (!multiFactor?.breakdown?.length) return null;
  const s = SIZES[size] ?? SIZES.large;
  const align = RING_ALIGN[size] ?? RING_ALIGN.large;

  const outOf = Math.round((multiFactor.perFactorMax ?? 10) * multiFactor.breakdown.length);
  const total = Math.round(multiFactor.total ?? 0);
  const pct = outOf > 0 ? Math.min(1, Math.max(0, total / outOf)) : 0;

  const r = (s.ring - s.ringStroke) / 2;
  const c = s.ring / 2;
  const circumference = 2 * Math.PI * r;

  const factorMax = multiFactor.perFactorMax || 10;
  const orderedBreakdown = sortOrder?.length
    ? [...multiFactor.breakdown].sort((a, b) => {
        const ai = sortOrder.indexOf(a.factor);
        const bi = sortOrder.indexOf(b.factor);
        // Factors not present in sortOrder (e.g. data drift between
        // entities) sink to the end, own-score sorted among themselves.
        if (ai === -1 && bi === -1) return (b.total ?? 0) - (a.total ?? 0);
        if (ai === -1) return 1;
        if (bi === -1) return -1;
        return ai - bi;
      })
    : [...multiFactor.breakdown].sort((a, b) => (b.total ?? 0) - (a.total ?? 0));
  const bars = orderedBreakdown.map((f) => ({ ...f, barPct: Math.min(1, Math.max(0, (f.total ?? 0) / factorMax)) }));

  const axisTicks = (() => {
    const step = factorMax / 5;
    return Array.from({ length: 6 }, (_, i) => Math.round(i * step * 10) / 10);
  })();

  return (
    <div style={{ display: "flex", alignItems: "flex-start", gap: s.gap, width: "100%", paddingLeft: align.inset, boxSizing: "border-box" }}>

      <div style={{ width: align.columnWidth, display: "flex", flexDirection: "column", alignItems: "center", flexShrink: 0 }}>
        <div style={{ position: "relative", width: s.ring, height: s.ring }}>
          <svg viewBox={`0 0 ${s.ring} ${s.ring}`} style={{ width: "100%", height: "100%", transform: "rotate(-90deg)" }}>
            <circle cx={c} cy={c} r={r} fill="none" stroke={INDEX_TRACK} strokeWidth={s.ringStroke} />
            <circle
              cx={c} cy={c} r={r} fill="none" stroke={INDEX_ACCENT} strokeWidth={s.ringStroke}
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={circumference * (1 - pct)}
            />
          </svg>
          <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
            <span style={{ fontWeight: 700, fontSize: s.totalFont, color: INDEX_TEXT, lineHeight: 1 }}>{total}</span>
            <span style={{ fontSize: s.outOfFont, color: INDEX_TEXT_MUTED, marginTop: 2 }}>out of {outOf}</span>
          </div>
        </div>

        <div style={{ width: s.ring, textAlign: "center", marginTop: 8 }}>
          <span style={{ fontWeight: 700, fontSize: s.captionFont, color: INDEX_TEXT, lineHeight: 1.25 }}>
            Composite Index Score
          </span>
          {onInfoClick && (
            <MUIIconButton
              data-html2canvas-ignore
              size="small"
              onClick={onInfoClick}
              id={iconId}
              style={{ padding: 2, display: "inline-flex", verticalAlign: "middle", marginLeft: 2 }}
            >
              <InfoOutlinedIcon fontSize="small" />
            </MUIIconButton>
          )}
        </div>
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: s.barRowGap }}>
          {bars.map((f) => (
            <div key={f.factor} style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span
                title={f.factor}
                style={{
                  flex: `0 0 ${s.barLabelWidth}px`,
                  width: s.barLabelWidth,
                  fontSize: s.barLabelFont,
                  color: INDEX_TEXT_MUTED,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {f.factor}
              </span>
              <div style={{ flex: 1, minWidth: 0, position: "relative", height: s.barHeight, background: INDEX_TRACK, borderRadius: 999 }}>
                <div
                  style={{
                    position: "absolute",
                    top: 0, left: 0, bottom: 0,
                    width: `${f.barPct * 100}%`,
                    background: INDEX_ACCENT,
                    borderRadius: 999,
                  }}
                />
              </div>
              <span style={{ flexShrink: 0, width: 28, textAlign: "right", fontSize: s.valueFont, fontWeight: 600, color: INDEX_TEXT_MUTED }}>
                {(f.total ?? 0).toFixed(1)}
              </span>
            </div>
          ))}
        </div>

        <div style={{ display: "flex", marginTop: 4, paddingLeft: s.barLabelWidth + 8, paddingRight: 36 }}>
          {axisTicks.map((tick, i) => (
            <span
              key={i}
              style={{
                flex: i === 0 || i === axisTicks.length - 1 ? "0 0 auto" : 1,
                textAlign: i === 0 ? "left" : i === axisTicks.length - 1 ? "right" : "center",
                fontSize: s.axisFont,
                color: INDEX_TEXT_MUTED,
              }}
            >
              {tick}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
